import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import pluralize from 'pluralize'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { H6, P, SuccessBadge, GrayBadge } from '@PipelineDeals/shared-react-components'

import { Section } from 'components/BusinessCard/components/StyledComponents'
import { useBusinessCard } from 'components/BusinessCard/context'

import CreatedUpdated from 'components/BusinessCard/components/CreatedUpdated'
import Description from 'components/BusinessCard/components/Description'
import ProjectOwner from 'components/BusinessCard/components/ProjectOwner'
import ProjectMilestones from 'components/BusinessCard/components/ProjectMilestones'
import ProjectCollaborators from 'components/BusinessCard/components/ProjectCollaborators'
import SimpleSection from 'components/BusinessCard/components/SimpleSection'
import { formatDate } from 'utils/date'

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.gray[200]};
  margin: 10px 0;
`

const StatusBadge = ({ status }) => {
  const { t } = useTranslation()
  if (status === 'open' || status === 0) {
    return <SuccessBadge content={t('businessCard.status.open')} />
  }
  return <GrayBadge content={t('businessCard.status.closed')} />
}

StatusBadge.propTypes = {
  status: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const Project = () => {
  const { t } = useTranslation()
  const {
    cardMeta: {
      showOnlyFields,
      showMainDivider
    },
    resource: project
  } = useBusinessCard()

  const linkedItemsSummary = useMemo(() => {
    if (!showOnlyFields.includes('counts') || !project.counts) return null

    const parts = []
    if (project.counts.deals > 0) {
      parts.push(`${project.counts.deals} ${pluralize('Deal', project.counts.deals)}`)
    }
    if (project.counts.people > 0) {
      parts.push(`${project.counts.people} ${pluralize('Person', project.counts.people)}`)
    }
    if (project.counts.companies > 0) {
      parts.push(`${project.counts.companies} ${pluralize('Company', project.counts.companies)}`)
    }

    if (parts.length === 0) return null

    return (
      <Section>
        <H6>{t('businessCard.labels.linkedItems')}</H6>
        <P>{parts.join(', ')}</P>
      </Section>
    )
  }, [project, showOnlyFields])

  const collaboratorCount = useMemo(() => {
    if (!project.counts?.collaborators) return null

    return `${project.counts.collaborators} ${pluralize('Collaborator', project.counts.collaborators)}`
  }, [project])

  const milestoneCount = useMemo(() => {
    if (!project.counts?.milestones) return null

    return `${project.counts.milestones} ${pluralize('Milestone', project.counts.milestones)}`
  }, [project])

  return (
    <>
      <ProjectOwner />

      <SimpleSection
        field="status"
        value={project.status !== undefined ? <StatusBadge status={project.status} /> : null}
        label={t('businessCard.labels.status')}
      />

      <SimpleSection
        field="start_date"
        value={project.start_date ? formatDate(project.start_date) : null}
        label={t('businessCard.labels.startDate')}
      />

      <SimpleSection
        field="end_date"
        value={project.end_date ? formatDate(project.end_date) : null}
        label={t('businessCard.labels.endDate')}
      />

      {showMainDivider && <Divider />}

      <Description />

      {linkedItemsSummary}

      <SimpleSection
        field="collaborator_count"
        value={collaboratorCount}
        label={t('businessCard.labels.collaborators')}
      />

      <SimpleSection
        field="milestone_count"
        value={milestoneCount}
        label={t('businessCard.labels.milestones')}
      />

      <ProjectMilestones />
      <ProjectCollaborators />

      <SimpleSection
        field="is_archived"
        value={project.is_archived ? t('businessCard.values.yes') : null}
        label={t('businessCard.labels.archived')}
      />

      <CreatedUpdated />
    </>
  )
}

export default Project
